import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, HStack, VStack } from '@sgme/ui';
import clsx from 'clsx';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { OptionHeader } from '@/App/tiles/Option/OptionHeader.tsx';
import { OptionMainPanel } from '@/App/tiles/Option/OptionMainPanel.tsx';

import './option.css';
import { useQueuedDispatch } from '@/App/tiles/Option/QueueContext.tsx';
import { StrategyPanel } from '@/App/tiles/Option/strategy/StrategyPanel.tsx';
import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import { ExecutionStatus } from '@/App/tiles/shared/ExecutionStatus.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { optionSlice } from '@/store/option/slice.ts';
import { resetOptionThunk } from '@/store/option/thunks/thunk.ts';
import { createWeakSelector } from '@/store/storeMethods.ts';
import { selectActiveTabZoomedTileId, setZoomedTileIdForActiveTab } from '@/store/tabs/slice.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

export type CashTileProps = {
  tileId: string;
};

export function OptionTile(props: CashTileProps) {
  const { tileId } = props;
  const tileW = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));
  const isZoomed = useAppSelector(selectActiveTabZoomedTileId) === tileId;

  const { currencyPair, strategyPanelMode, tradeCaptureError, executionStatus } = useAppSelector((state) =>
    selectOptionData(state, tileId),
  );
  const dispatch = useAppDispatch();
  const queuedDispatch = useQueuedDispatch();

  useEffect(() => {
    const w = strategyPanelMode !== 'hidden' ? 3 : 1;
    if (w !== tileW) {
      dispatch(tilesSlice.actions.resizeTile({ tileId, w }));
    }
  }, [tileW, strategyPanelMode, tileId]);

  async function handleReset() {
    await queuedDispatch(resetOptionThunk({ tileId, currencyPair, newType: 'Vanilla' }));
  }

  function deZoom() {
    dispatch(setZoomedTileIdForActiveTab({ tileId: undefined }));
  }

  if (tradeCaptureError) {
    return <ErrorTile tileId={tileId} onReset={handleReset} />;
  }

  if (isDefined(executionStatus)) {
    return <ExecutionStatus status={executionStatus} />;
  }

  if (isNotDefined(currencyPair)) {
    return (
      <div className="d-flex w-100 h-100">
        <HStack fullHeight fullWidth className="bg-lvl1">
          <VStack verticalAlignment="start" fullHeight>
            <OptionHeader tileId={tileId} />
          </VStack>
        </HStack>
      </div>
    );
  }

  return (
    <AutoAdvanceForm autoFocusIndex={1} className={clsx('d-flex flex-column', isZoomed && 'bg-lvl1 w-100 h-100')}>
      {isZoomed && (
        <div>
          <Button onClick={deZoom}>
            <em className="icon">arrow_back</em>
            <FormattedMessage id="zoomModeLayout.backToWorkspace" />
          </Button>
        </div>
      )}

      <div className="bg-lvl1 d-flex">
        <OptionMainPanel tileId={tileId} />
        {strategyPanelMode !== 'hidden' && <StrategyPanel tileId={tileId} />}
      </div>
    </AutoAdvanceForm>
  );
}

const selectOptionData = createWeakSelector([optionSlice.selectors.getOptionTile], (option) => {
  const tradeCapture = option?.tradeCapture;
  const strategyPanelMode = option?.properties.uiState.strategyPanelMode;

  return {
    currencyPair: option?.properties.currencyPair,
    strategyPanelMode: strategyPanelMode,
    tradeCaptureError: tradeCapture?.status === 'ERROR' ? tradeCapture.error : undefined,
    executionStatus: option?.execution?.status,
  };
});
